import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { RewardsApi } from '../API/RewardsApi';
import { WalletApi } from '../API/WalletApi';
import { useAuth } from '../Auth/AuthProvider';
import { getXPLevelInfo, XPLevelInfo } from '../Constants/xp';
import { Money, Transaction } from '../Types/Money';
import { UserType } from '../Types/User';

export enum Currency {
  XP = 'xp',
  COINS = 'points',
}

interface BalanceContextType {
  getBalance: (currency: Currency) => number;
  addBalance: (amount: number, currency: Currency) => void;
  withdrawBalance: (amount: number, currency: Currency) => void;
  isLoadingBalances: boolean;
  getRewardsByEventKey: (eventKey: string) => Promise<Transaction[]>;
  updateBalances: () => void;
  xpLevelInfo: XPLevelInfo;
}

const BalanceContext = createContext<BalanceContextType | undefined>(undefined);

interface BalanceProviderProps {
  children: ReactNode;
}

export const BalanceProvider: React.FC<BalanceProviderProps> = ({
  children,
}) => {
  const { userId, userType, userAuthorizationInfo } = useAuth();
  const [balances, setBalances] = useState<Money[]>();
  const [forceUpdateBalances, setForceUpdateBalances] = useState(0);
  const [isLoadingBalances, setIsLoadingBalances] = useState(true);

  useEffect(() => {
    if (
      !userId ||
      userType !== UserType.Student ||
      !userAuthorizationInfo?.canAccessStudentUI
    ) {
      setIsLoadingBalances(false);
      return;
    }

    const fetchBalances = async () => {
      try {
        setIsLoadingBalances(true);
        const walletApi = new WalletApi();
        const balances = await walletApi.getBalance();
        setBalances(Array.isArray(balances) ? balances : [balances]);
      } catch (error) {
        console.error('Failed to fetch balances:', error);
      } finally {
        setIsLoadingBalances(false);
      }
    };

    fetchBalances();
  }, [userId, userAuthorizationInfo, forceUpdateBalances]);

  const updateBalances = () => {
    setForceUpdateBalances((prev) => prev + 1);
  };

  const getRewardsByEventKey = async (
    eventKey: string,
  ): Promise<Transaction[]> => {
    const rewardsApi = new RewardsApi();
    return await rewardsApi.getRewardsByEventKey(eventKey);
  };

  const addBalance = (amount: number, currency: Currency) =>
    setBalances((oldBalances) =>
      oldBalances?.map((money) =>
        money.currency === currency
          ? { ...money, value: money.value + amount }
          : money,
      ),
    );

  const withdrawBalance = (amount: number, currency: Currency) =>
    setBalances((oldBalances) =>
      oldBalances?.map((money) =>
        money.currency === currency
          ? { ...money, value: money.value - amount }
          : money,
      ),
    );

  const getBalance = (currency: Currency) =>
    balances?.find((balance) => balance.currency === currency)?.value ?? 0;

  const xpLevelInfo = balances
    ? getXPLevelInfo(getBalance(Currency.XP))
    : undefined;

  return (
    <BalanceContext.Provider
      value={{
        getBalance,
        addBalance,
        withdrawBalance,
        getRewardsByEventKey,
        isLoadingBalances,
        updateBalances,
        xpLevelInfo,
      }}
    >
      {children}
    </BalanceContext.Provider>
  );
};

export const useBalance = (): BalanceContextType => {
  const context = useContext(BalanceContext);
  if (context === undefined) {
    throw new Error('useBalance must be used within a BalanceProvider');
  }
  return context;
};
